import React from 'react';
import { Alert, Box, Link as MuiLink, Typography } from '@mui/material';
import { isLegacyPreviewHost, LEGACY_HOST_BANNER_HEIGHT_VAR } from '../../constants/legacyPreviewHost';

const NEW_APP_ORIGIN = 'https://app2.unitresco.com';
const SESSION_DISMISS_KEY = 'ui:legacyHostBanner:dismissed';

/**
 * Informacja na wyłącznie app.unitresco.com: produkcja pod app2; logowanie nadal na tym hoście.
 */
const LegacyHostBanner: React.FC = () => {
  const [dismissed, setDismissed] = React.useState(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const syncHeight = React.useCallback(() => {
    if (dismissed || !isLegacyPreviewHost()) {
      try {
        document.documentElement.style.removeProperty(LEGACY_HOST_BANNER_HEIGHT_VAR);
      } catch {
        /* ignore */
      }
      return;
    }
    const h = rootRef.current?.offsetHeight ?? 0;
    try {
      document.documentElement.style.setProperty(LEGACY_HOST_BANNER_HEIGHT_VAR, `${h}px`);
    } catch {
      /* ignore */
    }
  }, [dismissed]);

  React.useLayoutEffect(() => {
    syncHeight();
  }, [syncHeight]);

  React.useEffect(() => {
    if (dismissed || !isLegacyPreviewHost()) return;
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => syncHeight());
    ro.observe(el);
    return () => ro.disconnect();
  }, [dismissed, syncHeight]);

  React.useEffect(() => {
    const onResize = () => syncHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncHeight]);

  if (!isLegacyPreviewHost()) return null;

  if (dismissed) {
    return null;
  }

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2147483600,
      }}
    >
      <Alert
        severity="warning"
        variant="filled"
        onClose={() => {
          try {
            sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
          } catch {
            /* ignore */
          }
          setDismissed(true);
          try {
            document.documentElement.style.removeProperty(LEGACY_HOST_BANNER_HEIGHT_VAR);
          } catch {
            /* ignore */
          }
        }}
        sx={{
          borderRadius: 0,
          alignItems: 'center',
          py: 0.5,
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.25 }}>
          Serwer produkcyjny został przeniesiony
        </Typography>
        <Typography variant="body2" component="div" sx={{ opacity: 0.95 }}>
          Ta strona (<strong>app.unitresco.com</strong>) jest tylko do podglądu. Do codziennej pracy używaj{' '}
          <MuiLink href={NEW_APP_ORIGIN} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit" sx={{ fontWeight: 700 }}>
            app2.unitresco.com
          </MuiLink>
          . Możesz się tutaj zalogować, jeśli potrzebujesz dostępu do tej instancji.
        </Typography>
      </Alert>
    </Box>
  );
};

export default LegacyHostBanner;
